import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { receivingLotService } from '@/services/receiving-lot.service';
import { ROUTES } from '@/routes/routes';

interface BulkImportResultLinksProps {
  resultingReceivingLotId: string | null;
  resultingArrivalId: string | null;
}

/**
 * Phase 2f-F (§10): "Open Receiving Lot" uses the existing, already-approved
 * `OPS_RECEIVING_LOT_DETAIL` route — never a constructed/guessed URL. No
 * standalone Arrival route exists anywhere in this application (confirmed
 * by inspecting `routes/routes.ts`), so the Arrival reference is rendered
 * as plain, non-clickable information — never an invented route.
 */
export function BulkImportResultLinks({
  resultingReceivingLotId,
  resultingArrivalId,
}: BulkImportResultLinksProps) {
  const { data: lot } = useQuery({
    queryKey: ['receiving-lot', resultingReceivingLotId],
    queryFn: () => receivingLotService.get(resultingReceivingLotId!),
    enabled: !!resultingReceivingLotId,
  });

  if (!resultingReceivingLotId) return null;

  return (
    <div className="flex flex-wrap items-center gap-4 text-sm">
      <Link
        to={ROUTES.OPS_RECEIVING_LOT_DETAIL.replace(':id', resultingReceivingLotId)}
        className="inline-flex items-center gap-1.5 font-medium text-primary hover:underline"
      >
        Open Receiving Lot{lot ? ` ${lot.lotNumber}` : ''}
        <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="h-3 w-3" />
      </Link>
      {resultingArrivalId && (
        <span className="text-xs text-muted-foreground">
          Arrival reference: <span className="font-mono">{resultingArrivalId}</span>
        </span>
      )}
    </div>
  );
}
